import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Phone, Mail, Loader2, Camera, Images } from "lucide-react";
import { toast } from "sonner";
import { contactFields } from "@/lib/contact";
import { api, formatError } from "@/lib/api";
import CameraCapture from "@/components/CameraCapture";

const join = (rec, cols) => cols.map((c) => rec.data[c.name]).filter((v) => v !== null && v !== undefined && v !== "").join(", ");
const ckey = (name) => String(name || "").replace(/\s+/g, " ").trim().toLowerCase();

function Line({ icon: Icon, text }) {
  if (!text) return null;
  return (
    <p className="flex gap-2 text-sm text-muted-foreground">
      <Icon size={14} className="shrink-0 mt-0.5 text-cyan-400" />
      <span className="line-clamp-2">{text}</span>
    </p>
  );
}

export default function CardsView({ columns, records, loading, onOpen, base, qboSynced }) {
  const f = contactFields(columns);
  const navigate = useNavigate();
  const [camFor, setCamFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState({});
  const [nameCol, setNameCol] = useState(null);
  const fileInput = useRef(null);
  const pickFor = useRef(null);

  const ids = records.map((r) => r.id);
  const loadCounts = () => {
    if (!base || !ids.length) return;
    api.post(`${base}/scan-counts`, { ids: ids.slice(0, 1000) }).then((r) => { setCounts(r.data.counts || {}); setNameCol(r.data.column || null); }).catch(() => {});
  };
  useEffect(loadCounts, [base, records]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFolder = (rec) => {
    const name = nameCol ? rec.data[nameCol] : rec.data[f.title?.name];
    navigate(`/customers?key=${encodeURIComponent(ckey(name))}&name=${encodeURIComponent(String(name || ""))}`);
  };

  const uploadFiles = async (rec, files) => {
    if (!rec || !files.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file, file.name);
      try { await api.post(`${base}/records/${rec.id}/scans`, form); ok += 1; }
      catch (e) { toast.error(`${file.name}: ${formatError(e)}`); }
    }
    if (ok) toast.success(`${ok} photo${ok === 1 ? "" : "s"} saved to customer's folder`);
    setBusy(false);
    loadCounts();
  };

  const openPicker = (rec) => { pickFor.current = rec; fileInput.current.click(); };

  return (
    <div className="relative min-h-[200px]" data-testid="cards-view">
      {(loading || busy) && <div className="absolute inset-0 bg-background/40 flex items-center justify-center z-10"><Loader2 className="animate-spin text-primary" /></div>}
      <input ref={fileInput} type="file" accept="image/*,application/pdf" multiple className="hidden"
        onChange={(e) => { const files = [...e.target.files]; const rec = pickFor.current; e.target.value = ""; uploadFiles(rec, files); }}
        data-testid="cards-photo-input" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {records.map((r, i) => {
          const title = r.data[f.title?.name] ?? "Untitled";
          return (
            <div key={r.id} role="button" tabIndex={0} onClick={() => onOpen(r)}
              onKeyDown={(e) => { if (e.key === "Enter") onOpen(r); }} data-testid={`contact-card-${i}`}
              className="relative min-w-0 text-left border rounded-lg bg-card p-5 hover:border-primary transition-colors fade-up space-y-3 cursor-pointer" style={{ animationDelay: `${i * 25}ms` }}>
              {base && (
                <div className="absolute top-3 right-3 flex gap-1.5">
                  <button type="button" title="Add photos for this customer"
                    onClick={(e) => { e.stopPropagation(); openPicker(r); }}
                    data-testid={`contact-card-photos-${i}`}
                    className="h-8 w-8 flex items-center justify-center rounded-md border bg-background/70 text-muted-foreground hover:text-primary hover:border-primary transition-colors">
                    <Images size={15} />
                  </button>
                  <button type="button" title="Take photo for this customer"
                    onClick={(e) => { e.stopPropagation(); setCamFor(r); }}
                    data-testid={`contact-card-camera-${i}`}
                    className="h-8 w-8 flex items-center justify-center rounded-md border bg-background/70 text-muted-foreground hover:text-primary hover:border-primary transition-colors">
                    <Camera size={15} />
                  </button>
                </div>
              )}
              <div className="flex items-start gap-3 pr-20">
                <div className="h-10 w-10 shrink-0 rounded-md bg-primary/15 text-primary font-display font-bold flex items-center justify-center">
                  {String(title).trim().charAt(0).toUpperCase() || "?"}
                </div>
                <div className="min-w-0">
                  <p className="font-display text-lg font-bold leading-tight truncate" data-testid={`contact-card-title-${i}`}>{String(title)}</p>
                  {f.subtitle && r.data[f.subtitle.name] && <p className="text-xs text-muted-foreground truncate">{r.data[f.subtitle.name]}</p>}
                  {counts[r.id] > 0 && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); openFolder(r); }}
                      title="Open this customer's photo folder"
                      className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[11px] font-medium hover:bg-primary/25 transition-colors" data-testid={`contact-card-scan-count-${i}`}>
                      <Images size={11} /> {counts[r.id]} scan{counts[r.id] === 1 ? "" : "s"}
                    </button>
                  )}
                  {qboSynced && qboSynced[r.id] && (
                    <span className="inline-flex items-center gap-1 mt-1 ml-1 px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 text-[11px] font-medium" data-testid={`contact-card-qbo-${i}`}>
                      <Images size={11} /> ✓ in QuickBooks
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Line icon={MapPin} text={join(r, f.address)} />
                <Line icon={Phone} text={join(r, f.phones)} />
                <Line icon={Mail} text={join(r, f.emails)} />
              </div>
            </div>
          );
        })}
      </div>
      {!loading && records.length === 0 && <p className="p-10 text-center text-muted-foreground" data-testid="cards-empty">No records found.</p>}
      <CameraCapture open={!!camFor} onClose={() => setCamFor(null)} onCapture={(file) => { const rec = camFor; setCamFor(null); uploadFiles(rec, [file]); }} />
    </div>
  );
}
